import { useApp } from "@/contexts/AppContext";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Edit,
  Trash2,
  Plus,
  FolderTree,
  Search,
  Move,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchThematicCategoryCounts,
  fetchThematicCategoryTree,
  type ThematicCategoryCountNode,
  type ThematicCategoryDto,
} from "@/services/searchPage.service";
import {
  deleteCategory,
  updateCategory,
  createCategory,
  moveCategoryWebsites,
} from "@/services/categoryAdmin.service";
import { CategoryCombobox } from "../website-suggest/CategoryCombobox";
import { MAX_CATEGORY_DEPTH, MAX_CATEGORY_PATH_SEGMENTS } from "@/constants/category";

type CategoryRow = {
  slug: string;
  nameDe: string;
  nameEn: string;
  displayName: string;
  depth: number;
  subcategoryCount: number;
  websiteCount: number;
};

type VisibleCategoryTreeRow = {
  category: CategoryRow;
  hasChildren: boolean;
  expanded: boolean;
  matchesSearch: boolean;
};

type MoveCategoryForm = {
  category1Id: string | null;
  category1Name: string;
  category1Depth?: number | null;
  categoryPathIds?: string[];
  categoryPathNames?: string[];
};

const areSetsEqual = <T,>(left: Set<T>, right: Set<T>) => {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }
  return true;
};

const matchesCategorySearch = (row: CategoryRow, term: string) => {
  if (!term) return true;
  const normalized = term.toLowerCase();
  return (
    row.displayName.toLowerCase().includes(normalized) ||
    row.nameDe.toLowerCase().includes(normalized) ||
    row.nameEn.toLowerCase().includes(normalized) ||
    row.slug.toLowerCase().includes(normalized)
  );
};

/**
 * Admin panel view for managing categories.
 */
export function CategoryManagementPanel() {
  const { t, language } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const [categoryTree, setCategoryTree] = useState<ThematicCategoryDto[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<
    ThematicCategoryCountNode[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryRow | null>(
    null
  );
  const [editForm, setEditForm] = useState({ nameDe: "", nameEn: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [createParent, setCreateParent] = useState<CategoryRow | null>(null);
  const [createForm, setCreateForm] = useState({ nameDe: "", nameEn: "" });
  const [isCreating, setIsCreating] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [movingCategory, setMovingCategory] = useState<CategoryRow | null>(null);
  const [moveForm, setMoveForm] = useState<MoveCategoryForm>({
    category1Id: null,
    category1Name: "",
    category1Depth: null,
    categoryPathIds: [],
    categoryPathNames: [],
  });
  const [isMoving, setIsMoving] = useState(false);
  const [openMoveCategory, setOpenMoveCategory] = useState(false);

  const isGerman = (language || "").toLowerCase().startsWith("de");

  const pickLocalizedName = (node: ThematicCategoryDto) =>
    isGerman ? node.nameDe : node.nameEn;

  const countsBySlug = useMemo(() => {
    const map = new Map<string, number>();
    const stack = [...categoryCounts];
    while (stack.length) {
      const current = stack.pop();
      if (!current) continue;
      map.set(current.slug, Number(current.count ?? 0));
      if (current.children?.length) {
        stack.push(...current.children);
      }
    }
    return map;
  }, [categoryCounts]);

  const totalsBySlug = useMemo(() => {
    const totals = new Map<string, number>();

    const computeTotal = (node: ThematicCategoryDto): number => {
      const ownCount = countsBySlug.get(node.slug) ?? 0;
      const children = node.children ?? [];
      const childSum = children.reduce(
        (sum, child) => sum + computeTotal(child),
        0
      );
      let total = ownCount;
      if (childSum > 0) {
        total = ownCount === childSum ? ownCount : ownCount + childSum;
      }
      totals.set(node.slug, total);
      return total;
    };

    categoryTree.forEach((node) => computeTotal(node));
    return totals;
  }, [categoryTree, countsBySlug]);

  const categoryRows = useMemo(() => {
    const rows: CategoryRow[] = [];

    const walk = (
      node: ThematicCategoryDto,
      depth: number
    ) => {
      const children = node.children ?? [];
      rows.push({
        slug: node.slug,
        nameDe: node.nameDe,
        nameEn: node.nameEn,
        displayName: pickLocalizedName(node),
        depth,
        subcategoryCount: children.length,
        websiteCount: totalsBySlug.get(node.slug) ?? 0,
      });
      children.forEach((child) => walk(child, depth + 1));
    };

    categoryTree.forEach((node) => walk(node, 0));
    return rows;
  }, [categoryTree, totalsBySlug, language]);

  const categoryRowBySlug = useMemo(
    () => new Map(categoryRows.map((row) => [row.slug, row])),
    [categoryRows]
  );

  useEffect(() => {
    if (categoryTree.length === 0) {
      setExpandedSlugs((previous) =>
        previous.size === 0 ? previous : new Set<string>()
      );
      return;
    }
    const validSlugs = new Set(categoryRows.map((row) => row.slug));
    const fallbackRoots = new Set(categoryTree.map((node) => node.slug));
    setExpandedSlugs((previous) => {
      const next = new Set<string>();
      previous.forEach((slug) => {
        if (validSlugs.has(slug)) {
          next.add(slug);
        }
      });
      if (next.size === 0) {
        fallbackRoots.forEach((slug) => next.add(slug));
      }
      return areSetsEqual(previous, next) ? previous : next;
    });
  }, [categoryTree, categoryRows]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const searchMatchSlugs = useMemo(() => {
    if (!normalizedSearchTerm) {
      return new Set<string>();
    }
    const matches = new Set<string>();
    for (const row of categoryRows) {
      if (matchesCategorySearch(row, normalizedSearchTerm)) {
        matches.add(row.slug);
      }
    }
    return matches;
  }, [categoryRows, normalizedSearchTerm]);

  const visibleSearchSlugs = useMemo(() => {
    if (!normalizedSearchTerm) {
      return new Set<string>();
    }
    const visible = new Set<string>();
    const visit = (node: ThematicCategoryDto): boolean => {
      const selfMatch = searchMatchSlugs.has(node.slug);
      const childMatch = (node.children ?? []).some((child) => visit(child));
      const subtreeMatch = selfMatch || childMatch;
      if (subtreeMatch) {
        visible.add(node.slug);
      }
      return subtreeMatch;
    };
    categoryTree.forEach((node) => {
      visit(node);
    });
    return visible;
  }, [categoryTree, normalizedSearchTerm, searchMatchSlugs]);

  const visibleRows = useMemo(() => {
    const rows: VisibleCategoryTreeRow[] = [];
    const searchMode = normalizedSearchTerm.length > 0;

    const append = (node: ThematicCategoryDto) => {
      const category = categoryRowBySlug.get(node.slug);
      if (!category) {
        return;
      }

      if (searchMode && !visibleSearchSlugs.has(node.slug)) {
        return;
      }

      const hasChildren = (node.children?.length ?? 0) > 0;
      const expanded = hasChildren
        ? searchMode || expandedSlugs.has(node.slug)
        : false;

      rows.push({
        category,
        hasChildren,
        expanded,
        matchesSearch: searchMode && searchMatchSlugs.has(node.slug),
      });

      if (hasChildren && expanded) {
        node.children?.forEach((child) => append(child));
      }
    };

    categoryTree.forEach((node) => append(node));
    return rows;
  }, [
    categoryTree,
    categoryRowBySlug,
    expandedSlugs,
    normalizedSearchTerm,
    searchMatchSlugs,
    visibleSearchSlugs,
  ]);

  useEffect(() => {
    let active = true;

    const loadCategories = async () => {
      setIsLoading(true);
      try {
        const [tree, counts] = await Promise.all([
          fetchThematicCategoryTree(),
          fetchThematicCategoryCounts(),
        ]);
        if (!active) return;
        setCategoryTree(tree);
        setCategoryCounts(counts);
      } catch (error) {
        console.error("Failed to load categories", error);
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void loadCategories();

    return () => {
      active = false;
    };
  }, []);

  const refreshCategories = async () => {
    setIsLoading(true);
    try {
      const [tree, counts] = await Promise.all([
        fetchThematicCategoryTree(),
        fetchThematicCategoryCounts(),
      ]);
      setCategoryTree(tree);
      setCategoryCounts(counts);
    } catch (error) {
      console.error("Failed to refresh categories", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async (category: CategoryRow) => {
    await deleteCategory(category.slug);
    toast.success(
      t("admin.categoryDeleted").replace("{name}", category.displayName)
    );
    await refreshCategories();
  };

  const handleEditOpen = (category: CategoryRow) => {
    setEditingCategory(category);
    setEditForm({ nameDe: category.nameDe, nameEn: category.nameEn });
    setEditOpen(true);
  };

  const handleCreateOpen = (parent: CategoryRow | null) => {
    if (parent && parent.depth >= MAX_CATEGORY_DEPTH) {
      toast.error(
        t("admin.categoryDepthLimitReached").replace(
          "{max}",
          String(MAX_CATEGORY_PATH_SEGMENTS)
        )
      );
      return;
    }
    setCreateParent(parent);
    setCreateForm({ nameDe: "", nameEn: "" });
    setCreateOpen(true);
  };

  const handleMoveOpen = (category: CategoryRow) => {
    setMovingCategory(category);
    setMoveForm({
      category1Id: null,
      category1Name: "",
      category1Depth: null,
      categoryPathIds: [],
      categoryPathNames: [],
    });
    setOpenMoveCategory(false);
    setMoveOpen(true);
  };

  const handleSave = async () => {
    if (!editingCategory) return;
    if (!editForm.nameDe.trim() || !editForm.nameEn.trim()) {
      toast.error(t("common.genericError"));
      return;
    }
    setIsSaving(true);
    try {
      const response = await updateCategory(editingCategory.slug, {
        nameDe: editForm.nameDe.trim(),
        nameEn: editForm.nameEn.trim(),
      });
      if (response.status === 409) {
        toast.error(t("admin.categoryNameExists"));
        return;
      }
      if (response.status === 400) {
        toast.error(
          t("admin.categoryDepthLimitReached").replace(
            "{max}",
            String(MAX_CATEGORY_PATH_SEGMENTS)
          )
        );
        return;
      }
      if (response.status < 200 || response.status >= 300) {
        toast.error(t("common.genericError"));
        return;
      }
      toast.success(t("admin.categoryUpdated"));
      setEditOpen(false);
      setEditingCategory(null);
      await refreshCategories();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.nameDe.trim() || !createForm.nameEn.trim()) {
      toast.error(t("common.genericError"));
      return;
    }
    if (createParent && createParent.depth >= MAX_CATEGORY_DEPTH) {
      toast.error(
        t("admin.categoryDepthLimitReached").replace(
          "{max}",
          String(MAX_CATEGORY_PATH_SEGMENTS)
        )
      );
      return;
    }
    setIsCreating(true);
    try {
      const response = await createCategory({
        nameDe: createForm.nameDe.trim(),
        nameEn: createForm.nameEn.trim(),
        parentSlug: createParent?.slug ?? null,
      });
      if (response.status === 409) {
        toast.error(t("admin.categoryNameExists"));
        return;
      }
      if (response.status < 200 || response.status >= 300) {
        toast.error(t("common.genericError"));
        return;
      }
      toast.success(t("admin.categoryCreated"));
      setCreateOpen(false);
      setCreateParent(null);
      await refreshCategories();
    } finally {
      setIsCreating(false);
    }
  };

  const handleMoveWebsites = async () => {
    if (!movingCategory) return;
    if (!moveForm.category1Id) {
      toast.error(t("admin.moveCategoryTargetRequired"));
      return;
    }
    setIsMoving(true);
    try {
      const response = await moveCategoryWebsites(movingCategory.slug, {
        targetCategoryId: moveForm.category1Id,
      });
      if (response.status < 200 || response.status >= 300) {
        toast.error(t("common.genericError"));
        return;
      }
      const movedCount = Number(response.data?.movedCount ?? 0);
      toast.success(
        t("admin.moveCategorySuccess").replace("{count}", String(movedCount))
      );
      setMoveOpen(false);
      setMovingCategory(null);
      await refreshCategories();
    } finally {
      setIsMoving(false);
    }
  };

  const toggleExpanded = (slug: string) => {
    if (normalizedSearchTerm) {
      return;
    }
    setExpandedSlugs((previous) => {
      const next = new Set(previous);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  return (
    <Card className="stat-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <CardTitle
              className="card-title-text flex items-center gap-2"
              style={primaryTextStyle}
            >
              <FolderTree className="w-5 h-5" style={iconStyle} />
              {t("admin.categoryManagementTitle")}
            </CardTitle>
            <CardDescription
              className="card-description-text"
              style={secondaryTextStyle}
            >
              {t("admin.categoryManagementFullDescription")}
            </CardDescription>
            <p className="text-xs text-secondary mt-2" style={secondaryTextStyle}>
              {t("admin.categoryDepthLimitHint").replace(
                "{max}",
                String(MAX_CATEGORY_PATH_SEGMENTS)
              )}
            </p>
          </div>
          <Button
            className="gap-2 w-full sm:w-auto"
            onClick={() => handleCreateOpen(null)}
          >
            <Plus className="w-4 h-4" />
            {t("admin.newCategory")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={iconStyle}
            />
            <Input
              placeholder={`${t("common.search")}...`}
              className="pl-9"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </div>
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px] text-secondary">
                  {t("admin.tableCategory")}
                </TableHead>
                <TableHead className="text-right min-w-[80px] text-secondary">
                  {t("admin.tableWebsites")}
                </TableHead>
                <TableHead className="text-right min-w-[100px] text-secondary">
                  {t("admin.tableActions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} style={secondaryTextStyle}>
                    {t("common.loading")}
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} style={secondaryTextStyle}>
                    {t("common.noResults")}
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map(({ category, hasChildren, expanded, matchesSearch }) => {
                  const canDelete =
                    category.subcategoryCount === 0 &&
                    category.websiteCount === 0;
                  const canAddChild = category.depth < MAX_CATEGORY_DEPTH;
                  const indentation = `${category.depth * 1.25}rem`;

                  return (
                    <TableRow key={category.slug}>
                      <TableCell style={primaryTextStyle} className="align-top">
                        <div
                          className="flex items-center gap-2 min-h-8"
                          style={{ paddingLeft: indentation }}
                        >
                          {hasChildren ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0"
                              onClick={() => toggleExpanded(category.slug)}
                              disabled={Boolean(normalizedSearchTerm)}
                              aria-label={
                                expanded
                                  ? t("admin.categoryTreeCollapseNode")
                                  : t("admin.categoryTreeExpandNode")
                              }
                            >
                              {expanded ? (
                                <ChevronDown className="w-4 h-4" style={iconStyle} />
                              ) : (
                                <ChevronRight className="w-4 h-4" style={iconStyle} />
                              )}
                            </Button>
                          ) : (
                            <span className="w-6 h-6 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div
                              className={`truncate ${matchesSearch ? "font-semibold" : ""}`}
                              style={matchesSearch ? { color: "color-mix(in srgb, var(--primary) 80%, var(--foreground))" } : undefined}
                            >
                              {category.displayName}
                            </div>
                            <div className="text-xs text-secondary truncate">
                              {category.slug}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        className="text-right align-top"
                        style={secondaryTextStyle}
                      >
                        {category.websiteCount}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEditOpen(category)}
                          >
                            <Edit className="w-4 h-4" style={iconStyle} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={category.websiteCount === 0}
                            onClick={() => handleMoveOpen(category)}
                          >
                            <Move className="w-4 h-4" style={iconStyle} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={!canAddChild}
                            title={
                              canAddChild
                                ? undefined
                                : t("admin.categoryDepthLimitReached").replace(
                                    "{max}",
                                    String(MAX_CATEGORY_PATH_SEGMENTS)
                                  )
                            }
                            onClick={() => handleCreateOpen(category)}
                          >
                            <Plus className="w-4 h-4" style={iconStyle} />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={!canDelete}
                                className="h-8 w-8"
                              >
                                <Trash2 className="w-4 h-4" style={iconStyle} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t("admin.deleteCategoryConfirmTitle")}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t("admin.deleteCategoryDescription")}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>
                                  {t("common.cancel")}
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteCategory(category)}
                                >
                                  {t("common.delete")}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <Dialog
          open={editOpen}
          onOpenChange={(open) => {
            setEditOpen(open);
            if (!open) {
              setEditingCategory(null);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin.editCategory")}</DialogTitle>
              <DialogDescription>
                {editingCategory?.displayName}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("admin.categoryNameGerman")}</Label>
                <Input
                  value={editForm.nameDe}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      nameDe: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("admin.categoryNameEnglish")}</Label>
                <Input
                  value={editForm.nameEn}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      nameEn: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
              >
                {t("common.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) {
              setCreateParent(null);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {createParent
                  ? t("admin.addSubcategory")
                  : t("admin.newCategory")}
              </DialogTitle>
              <DialogDescription>
                {createParent?.displayName ?? t("admin.noParent")}
              </DialogDescription>
              {createParent && createParent.depth >= MAX_CATEGORY_DEPTH && (
                <p className="text-sm text-secondary">
                  {t("admin.categoryDepthLimitReached").replace(
                    "{max}",
                    String(MAX_CATEGORY_PATH_SEGMENTS)
                  )}
                </p>
              )}
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("admin.categoryNameGerman")}</Label>
                <Input
                  value={createForm.nameDe}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      nameDe: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("admin.categoryNameEnglish")}</Label>
                <Input
                  value={createForm.nameEn}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      nameEn: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={
                  isCreating ||
                  (createParent != null && createParent.depth >= MAX_CATEGORY_DEPTH)
                }
              >
                {t("common.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={moveOpen}
          onOpenChange={(open) => {
            setMoveOpen(open);
            if (!open) {
              setMovingCategory(null);
              setOpenMoveCategory(false);
              setMoveForm({
                category1Id: null,
                category1Name: "",
                category1Depth: null,
                categoryPathIds: [],
                categoryPathNames: [],
              });
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin.moveCategoryTitle")}</DialogTitle>
              <DialogDescription>
                {t("admin.moveCategoryDescription").replace(
                  "{name}",
                  movingCategory?.displayName ?? ""
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <CategoryCombobox
                formData={moveForm}
                setFormData={setMoveForm}
                openCategory1={openMoveCategory}
                setOpenCategory1={setOpenMoveCategory}
                t={t}
                language={language}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleMoveWebsites}
                disabled={isMoving}
              >
                {t("admin.moveCategoryConfirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
